import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, Card } from "@/components/Shared";
import { ChatThread } from "@/components/ChatThread";
import { RecordHeader, RecordLayout, DetailPanel, Field, RelatedList, col, paymentsFromInvoices, PAYMENT_COLS } from "@/components/Record";
import { SlaChip, PRIORITY_STYLE } from "@/pages/Enquiries";
import { ENQUIRY_SOURCES } from "@/components/QuickCreate";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR, formatDateTime } from "@/lib/format";
import { Pause, Play, XCircle, MessageSquare, Clock, CheckCircle2, FileText, Phone } from "lucide-react";
import { Attachment } from "@/components/Attachment";
import { RecordTabs, ActivityPanel } from "@/components/Activities";
import { EnquiryCadences } from "@/components/CadenceWidget";
import { ContactActions } from "@/components/ContactActions";

const WORK_STATUSES = ["Assigned", "Preparing Quotation", "Internal Review"];
const fmt = (m) => (m == null ? "—" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function ReasonDialog({ open, title, onClose, onSubmit, required }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <Textarea data-testid="reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason…" />
        <DialogFooter><Button onClick={() => onSubmit(reason)} disabled={required && !reason.trim()} className="bg-[#101D51]" data-testid="reason-submit">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [tab, setTab] = useState("related");

  const load = useCallback(async () => {
    const { data } = await api.get(`/enquiries/${id}`);
    setE(data);
    if (data.conversation_id) setMessages((await api.get(`/conversations/${data.conversation_id}/messages`)).data);
  }, [id]);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, [load]);

  const act = async (path, body) => {
    try { await api.post(`/enquiries/${id}/${path}`, body || {}); toast.success("Updated"); setDlg(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!e) return <Loading />;
  const E = (field, type, extra = {}) => ({ collection: "enquiries", id, field, type, onSaved: load, ...extra });
  const closed = ["Converted To Order", "Lost"].includes(e.status);
  const c = e.clocks || {};
  const payments = paymentsFromInvoices(e.invoices);

  return (
    <div data-testid="enquiry-detail">
      <RecordHeader kind="enquiry" title={e.number} status={e.status}
        subtitle={<span className="flex items-center gap-2"><SlaChip enq={e} /><span className={`font-medium ${PRIORITY_STYLE[e.priority]}`}>{e.priority}</span><span>· {e.customer_name}</span>{e.customer?.phone && <ContactActions phone={e.customer.phone} size="sm" message={`Hi, regarding your enquiry ${e.number}`} />}</span>}
        actions={!closed && <>
          <Select value={e.assigned_to || ""} onValueChange={(v) => act("assign", { user_id: v })}>
            <SelectTrigger data-testid="assign-select" className="h-10 w-44"><SelectValue placeholder="Assign to…" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
          </Select>
          {e.assigned_to && e.status !== "On Hold" && e.status !== "Quote Sent" && (
            <Select value={e.status} onValueChange={(v) => act("status", { status: v })}>
              <SelectTrigger data-testid="status-select" className="h-10 w-48"><SelectValue /></SelectTrigger>
              <SelectContent>{WORK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>)}
          {e.status === "On Hold"
            ? <Button variant="outline" className="h-10" onClick={() => act("resume")} data-testid="resume-btn"><Play className="w-4 h-4" /> Resume</Button>
            : <Button variant="outline" className="h-10" onClick={() => setDlg("hold")} data-testid="hold-btn"><Pause className="w-4 h-4" /> On Hold</Button>}
          <Button variant="outline" className="h-10 text-[#F83A84] border-[#F83A84]/40" onClick={() => setDlg("lost")} data-testid="lost-btn"><XCircle className="w-4 h-4" /> Mark Lost</Button>
          {e.status !== "On Hold" && <Button className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" onClick={() => navigate(`/quotes/new?enquiry_id=${e.id}&customer_id=${e.customer_id}`)} data-testid="create-quote-btn"><FileText className="w-4 h-4" /> {e.quotes?.length ? "New version" : "Create quote"}</Button>}
        </>} />

      <RecordLayout
        left={<>
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-[#6B7189]">Receipt → Assigned</div>
              <div className="text-lg font-bold text-[#101D51] flex items-center gap-1">{fmt(c.to_assign_minutes)}{c.assignment_done && <CheckCircle2 className="w-4 h-4 text-[#0B7C5A]" />}</div></Card>
            <Card className="p-3"><div className="text-[11px] uppercase tracking-wide text-[#6B7189]">Assigned → Quote</div>
              <div className="text-lg font-bold text-[#101D51] flex items-center gap-1">{fmt(c.to_quote_minutes)}{c.quote_done && <CheckCircle2 className="w-4 h-4 text-[#0B7C5A]" />}</div>
              {c.sla_remaining_minutes != null && !c.quote_done && <div className={`text-[11px] ${c.sla_remaining_minutes < 0 ? "text-[#F83A84]" : "text-[#6B7189]"}`}>{c.sla_remaining_minutes < 0 ? `${fmt(-c.sla_remaining_minutes)} over` : `${fmt(c.sla_remaining_minutes)} left`}</div>}</Card>
          </div>
          <DetailPanel>
            <Field label="Enquiry number" value={e.number} mono />
            <Field label="Customer" value={e.customer_name} onClick={() => navigate(`/customers/${e.customer_id}`)} testid="enq-customer-link" />
            <Field label="Phone" value={e.customer?.phone ? `+91 ${e.customer.phone}` : null} />
            <Field label="Source" value={e.source} edit={!closed ? E("source", "select", { options: ENQUIRY_SOURCES }) : undefined} />
            <Field label="Priority" value={e.priority} edit={!closed ? E("priority", "select", { options: ["Standard", "High", "Urgent"] }) : undefined} />
            <Field label="Assigned to" value={e.assigned_name} />
            <Field label="Received" value={formatDateTime(e.received_at)} />
            <Field label="Assigned at" value={formatDateTime(e.assigned_at)} />
            <Field label="SLA due" value={formatDateTime(e.sla_due_at)} />
            <Field label="Quote sent at" value={formatDateTime(e.quote_sent_at)} />
            <Field label="Quote value" value={e.quote_value ? formatINR(e.quote_value) : null} />
            <Field label="Order value" value={e.order_value ? formatINR(e.order_value) : null} />
            {e.lost_reason && <Field label="Lost reason" value={e.lost_reason} />}
          </DetailPanel>
          <Card className="p-4">
            <Field label="Requirement" value={e.description} testid="enq-description" edit={!closed ? E("description", "textarea") : undefined} />
            {e.attachments?.length > 0 && (
              <div className="mt-3" data-testid="enq-attachments">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-2">Attachments · {e.attachments.length}</div>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  {e.attachments.map((a) => <Attachment key={a.message_id} m={{ id: a.message_id, type: a.type, media: a.media }} compact gallery={e.attachments} />)}
                </div>
              </div>)}
          </Card>
          <EnquiryCadences enquiryId={e.id} />
        </>}
        right={<>
          <RecordTabs tabs={[{ id: "related", label: "Related", count: e.quotes.length + e.orders.length + e.invoices.length }, { id: "activity", label: "Activities", icon: Phone }, { id: "history", label: "Status history", count: (e.status_history || []).length }, { id: "whatsapp", label: "WhatsApp", icon: MessageSquare, count: messages?.length || 0 }]} active={tab} onChange={setTab} testid="enq-tabs" />
          {tab === "related" && <>
          <RelatedList kind="quote" rows={e.quotes.map((q) => ({ ...q, path: `/quotes/${q.id}` }))} onNew={!closed && e.status !== "On Hold" ? () => navigate(`/quotes/new?enquiry_id=${e.id}&customer_id=${e.customer_id}`) : undefined}
            columns={[col.number(), col.status(), { key: "version", label: "Ver", render: (q) => `v${q.version}${q.is_primary ? " ★" : ""}` }, col.date(), col.money()]} />
          <RelatedList kind="order" title="Sales Orders" rows={e.orders.map((o) => ({ ...o, path: `/orders/${o.id}` }))}
            onNew={!closed ? () => navigate(`/orders/new?enquiry_id=${e.id}&customer_id=${e.customer_id}`) : undefined}
            columns={[col.number(), col.status(), col.text("customer_po_number", "Customer PO"), col.date(), col.money()]} />
          <RelatedList kind="invoice" rows={e.invoices.map((i) => ({ ...i, path: `/invoices/${i.id}` }))}
            onNew={!closed ? () => navigate(`/invoices/new?enquiry_id=${e.id}&customer_id=${e.customer_id}`) : undefined}
            columns={[col.number(), col.status(), col.date("due_date", "Due"), col.money(), col.money("balance_due", "Balance")]} />
          <RelatedList kind="payment" rows={payments} columns={PAYMENT_COLS} />
          </>}
          {tab === "activity" && <ActivityPanel object="enquiry" objectId={e.id} defaultSubject={e.customer_name} />}
          {tab === "whatsapp" && <Card className="overflow-hidden flex flex-col max-h-[70vh]">
            <div className="px-4 py-3 border-b border-[#E6E8F0] flex items-center justify-between">
              <div className="font-semibold text-[#101D51] flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#25D366]" /> WhatsApp thread</div>
              {e.conversation_id && <button onClick={() => navigate(`/inbox?c=${e.conversation_id}`)} className="text-xs text-[#0B7C5A] font-medium" data-testid="open-in-inbox">Open in Inbox</button>}
            </div>
            <div className="overflow-y-auto">{messages ? <ChatThread messages={messages} /> : <div className="p-6 text-sm text-[#6B7189] text-center">No linked WhatsApp conversation ({e.source}).</div>}</div>
          </Card>}
          {tab === "history" && <Card className="p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-3">Status history</div>
            <ol className="relative border-l border-[#E6E8F0] ml-2 space-y-3" data-testid="enq-timeline">
              {[...(e.status_history || [])].reverse().map((h, i) => (
                <li key={`${h.at}-${h.status}`} className="ml-4"><span className="absolute -left-1.5 w-3 h-3 rounded-full bg-[#101D51] border-2 border-white" />
                  <div className="text-sm font-medium text-[#101D51]">{h.status}{h.note ? <span className="text-[#6B7189] font-normal"> — {h.note}</span> : ""}</div>
                  <div className="text-[11px] text-[#6B7189] flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(h.at)}{h.by_name ? ` · ${h.by_name}` : ""}</div></li>))}
            </ol>
          </Card>}
        </>} />
      <ReasonDialog open={dlg === "hold"} title="Put enquiry on hold" onClose={() => setDlg(null)} onSubmit={(r) => act("hold", { reason: r })} />
      <ReasonDialog open={dlg === "lost"} title="Mark as lost" required onClose={() => setDlg(null)} onSubmit={(r) => act("lost", { reason: r })} />
    </div>
  );
}
